/* eslint-disable @typescript-eslint/camelcase */

import React, { useState, useContext } from 'react';
import memoizee from 'memoizee';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { selectIovationUrl } from 'state/iovation';

/**
 * This is memoized to return the same promise for each distinct iovationUrl
 */
const startIovation: (iovationUrl: string) => Promise<string> = memoizee(
  (iovationUrl: string) =>
    new Promise((resolve, reject) => {
      if (typeof window === 'undefined') {
        return;
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const anyWindow = window as any;

      anyWindow.io_install_stm = false; // do not install ActiveX
      anyWindow.io_install_flash = false; // do not install ActiveX
      anyWindow.io_exclude_stm = 12; // exclude Active X on XP and higher

      anyWindow.io_bb_callback = (blackbox: string, completed: unknown) => {
        if (completed) {
          resolve(blackbox);
        }
      };

      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.async = true;
      script.src = iovationUrl;
      script.onerror = () => {
        reject('Iovation error');
      };
      document.getElementsByTagName('body')[0].appendChild(script);
    }),
);

export const IovationContext = React.createContext<Promise<string> | null>(
  null,
);

const useIovation: () => Promise<string> | null = () => {
  return useContext(IovationContext);
};

/**
 * Set up the Iovation script.
 *
 * Use this high up (preferably in the top level App component ) so that it
 * can be triggered early.
 */
const IovationProvider: React.FC = ({ children }) => {
  const { enqueueSnackbar } = useSnackbar();
  const iovationUrl = useSelector(selectIovationUrl);
  const [promise] = useState<Promise<string>>(startIovation(iovationUrl));
  promise.catch(_ =>
    enqueueSnackbar(
      'We noticed you may be using an adblocker. Please disable it, then refresh the page to continue with your application.',
      {
        variant: 'error',
        persist: true,
      },
    ),
  );
  return (
    <IovationContext.Provider value={promise}>
      {children}
    </IovationContext.Provider>
  );
};

export { IovationProvider };

export default useIovation;
